/**
 *
 * @module sdkSession
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/sdkSession.md
 * @see https://github.bamtech.co/sdk-distribution/bam-sdk#feature-overviews
 * @see https://github.bamtech.co/sdk-distribution/bam-sdk/blob/master/Features/SdkSession.md
 * @see https://github.bamtech.co/sdk-distribution/bam-sdk/blob/master/Features/Error-Handling.md
 *
 */

/* eslint camelcase: "off" */

import Check from 'check-types';
import EventEmitter from 'events';

import Services from './services/entry';
import Account from './account/entry';
import Commerce from './commerce/entry';
import Configuration from './configuration/entry';
import Content from './content/entry';
import CustomerService from './customerService/entry';
import Device from './device/entry';
import Drm from './drm/entry';
import Identity from './identity/entry';
import Internal from './internal/entry';
import Media from './media/entry';
import Paywall from './paywall/entry';
import Purchase from './purchase/entry';
import Session from './session/entry';
import Subscription from './subscription/entry';
import Token from './token/entry';
import UserActivity from './userActivity/entry';
import UserProfile from './userProfile/entry';
import Logging from './logging/entry';

import DustSink from './internal/dust/dustSink';
import ReauthorizationFailure from './reauthorizationFailure';
import AdEngineManager from './media/adEngine/adEngineManager';
import AdEngineClient from './services/media/adEngine/adEngineClient';

const LoggingApi = Logging.LoggingApi;
const Util = Services.Util;
const Storage = Services.Storage;
const HttpClient = Services.HttpClient;
const BootstrapConfiguration = Services.Configuration.BootstrapConfiguration;
const SdkSessionConfiguration = Services.Configuration.SdkSessionConfiguration;
const AccountGrant = Services.Account.AccountGrant;
const DustLogUtility = Services.Internal.Dust.DustLogUtility;
const SdkSessionDustUrnReference = Services.Internal.Dust.DustUrnReference.sdkSession;

const AccountClient = Services.Account.AccountClient;
const AdobeIdentityClient = Services.Identity.Adobe.AdobeIdentityClient;
const BamIdentityClient = Services.Identity.Bam.BamIdentityClient;
const CommerceClient = Services.Commerce.CommerceClient;
const ContentClient = Services.Content.ContentClient;
const CustomerServiceClient = Services.CustomerService.CustomerServiceClient;
const DeviceClient = Services.Device.DeviceClient;
const DrmClient = Services.Drm.DrmClient;
const MediaClient = Services.Media.MediaClient;
const TokenClient = Services.Token.TokenClient;
const TelemetryClient = Services.Internal.Telemetry.TelemetryClient;
const PaywallClient = Services.Paywall.PaywallClient;
const PurchaseClient = Services.Purchase.PurchaseClient;
const SubscriptionClient = Services.Subscription.SubscriptionClient;
const SessionClient = Services.Session.SessionClient;

const AccountManager = Account.AccountManager;
const AdobeIdentityManager = Identity.Adobe.AdobeIdentityManager;
const BamIdentityManager = Identity.Bam.BamIdentityManager;
const CommerceManager = Commerce.CommerceManager;
const ContentManager = Content.ContentManager;
const ConfigurationManager = Configuration.ConfigurationManager;
const CustomerServiceManager = CustomerService.CustomerServiceManager;
const DeviceManager = Device.DeviceManager;
const DrmManager = Drm.DrmManager;
const MediaManager = Media.MediaManager;
const MediaCapabilitiesProvider = Media.MediaCapabilitiesProvider;
const TokenManager = Token.TokenManager;
const TelemetryManager = Internal.Telemetry.TelemetryManager;
const PaywallManager = Paywall.PaywallManager;
const PurchaseManager = Purchase.PurchaseManager;
const SubscriptionManager = Subscription.SubscriptionManager;
const SessionManager = Session.SessionManager;

const AccessContextState = Token.AccessContextState;
const AccessTokenProvider = Token.AccessTokenProvider;
const DeviceGrantStorage = Token.DeviceGrantStorage;
const AccessStorage = Token.AccessStorage;
const GeoProvider = Token.GeoProvider;
const IdentityTokenStorage = Token.IdentityTokenStorage;

const AccountApi = Account.AccountApi;
const AdobeIdentityApi = Identity.Adobe.AdobeIdentityApi;
const BamIdentityApi = Identity.Bam.BamIdentityApi;
const CommerceApi = Commerce.CommerceApi;
const ContentApi = Content.ContentApi;
const MediaApi = Media.MediaApi;
const PaywallApi = Paywall.PaywallApi;
const PurchaseApi = Purchase.PurchaseApi;
const SubscriptionApi = Subscription.SubscriptionApi;
const UserActivityApi = UserActivity.UserActivityApi;
const UserProfileApi = UserProfile.UserProfileApi;

const InvalidArgumentException = Services.Exception.InvalidArgumentException;
const ServiceException = Services.Exception.ServiceException;
const ExceptionReference = Services.Exception.ExceptionReference;
const ErrorReason = Services.Exception.ErrorReason;

const {
    assigned,
    instanceStrict,
    nonEmptyString
} = Check.assert;

let dustSink;

/**
 *
 * @class SdkSession
 * @extends {EventEmitter}
 * @see https://nodejs.org/api/events.html
 * @emits {ReauthorizationFailure} The event raised when automatic token refresh fails.
 * @desc Represents an SDK session for the application to interact with.
 *
 */
export default class SdkSession extends EventEmitter {

    /**
     *
     * @param {SDK.Services.Configuration.SdkSessionConfiguration} sdkSessionConfiguration
     * @param {SDK.Token.AccessStorage} accessStorage
     * @param {SDK.Logging.Logger} [logger=Logging]
     * @param {SDK.Token.GeoProvider} [geoProvider=null]
     * @param {SDK.Media.MediaCapabilitiesProvider} [mediaCapabilitiesProvider=null]
     * @param {SDK.Services.Storage} [storage=null]
     * @param {SDK.Services.HttpClient} [httpClient=null]
     * @param {Object} managers
     * @param {Object} metadata
     * @param {SDK.Token.AccessTokenProvider} accessTokenProvider
     * @throws {InvalidArgumentException}
     *
     */
    constructor(sdkSessionConfiguration, accessStorage, logger = Logging, geoProvider = null,
        mediaCapabilitiesProvider = null, storage = null, httpClient = null, managers, metadata, accessTokenProvider) {

        const errorMessage = {
            ctor: `SdkSession(sdkSessionConfiguration, accessStorage, logger, geoProvider, storage,
                httpClient, managers, metadata, accessTokenProvider)`,
            get sdkSessionConfiguration() {
                return `${this.ctor} needs SDK.Services.Configuration.SdkSessionConfiguration instance`;
            },
            get accessStorage() {
                return `${this.ctor} needs SDK.Token.AccessStorage instance`;
            },
            get storage() {
                return `${this.ctor} needs SDK.Services.Storage instance`;
            },
            get managersInstance() {
                return `${this.ctor} needs structured managers Object`;
            },
            get metadataInstance() {
                return `${this.ctor} needs structured session metadata Object`;
            },
            get accessTokenProvider() {
                return `${this.ctor} needs SDK.Token.AccessTokenProvider instance`;
            }
        };

        instanceStrict(sdkSessionConfiguration, SdkSessionConfiguration, errorMessage.sdkSessionConfiguration, InvalidArgumentException); // eslint-disable-line
        instanceStrict(accessStorage, AccessStorage, errorMessage.accessStorage, InvalidArgumentException);

        if (Check.assigned(storage)) {
            instanceStrict(storage, Storage, errorMessage.storage, InvalidArgumentException);
        }

        assigned(Util.getSafe(() => managers.tokenManager), errorMessage.managersInstance, InvalidArgumentException);
        assigned(Util.getSafe(() => metadata.version), errorMessage.metadataInstance, InvalidArgumentException);
        instanceStrict(accessTokenProvider, AccessTokenProvider, errorMessage.accessTokenProvider, InvalidArgumentException); // eslint-disable-line

        super();

        /**
         *
         * @access private
         * @type {SDK.Services.Configuration.SdkSessionConfiguration}
         *
         */
        this.config = sdkSessionConfiguration;

        /**
         *
         * @access private
         * @type {SDK.Token.AccessStorage}
         *
         */
        this.accessStorage = accessStorage;

        /**
         *
         * @access private
         * @type {SDK.Logging.Logger}
         * @desc Single Logger instance to be reused throughout the SDK.
         *
         */
        this.logger = logger;

        /**
         *
         * @access private
         * @type {SDK.Token.GeoProvider}
         *
         */
        this.geoProvider = geoProvider || new GeoProvider(this.logger);

        /**
         *
         * @access public
         * @type {SDK.Media.MediaCapabilitiesProvider}
         *
         */
        this.mediaCapabilitiesProvider = mediaCapabilitiesProvider || new MediaCapabilitiesProvider(this.logger);

        /**
         *
         * @access public
         * @type {SDK.Services.Storage}
         * @desc Single Storage instance to be reused throughout the SDK.
         * Exposed publicly for use in util modules, and to allow application developers
         * to reference the platform specific {SDK.Services.Storage} implementation.
         *
         */
        this.storage = storage || new Storage(this.logger);

        /**
         *
         * @access private
         * @since 2.0.0
         * @type {SDK.Services.HttpClient}
         * @desc Single HttpClient instance to be reused in all FeatureClient(s).
         *
         */
        this.httpClient = httpClient || new HttpClient(this.logger);

        /**
         *
         * @access private
         * @type {Object}
         * @desc Structured map with all expected managers.
         *
         */
        this.managers = managers;

        /**
         *
         * @access private
         * @type {SDK.Token.AccessTokenProvider}
         * @since 3.0.0
         *
         */
        this.accessTokenProvider = accessTokenProvider;

        /**
         *
         * @access private
         * @type {String}
         * @desc The public application key for the SDK.
         *
         */
        this.clientApiKey = metadata.clientApiKey || '';

        /**
         *
         * @access public
         * @type {String}
         * @since 1.1.2
         * @desc The client ID to use the SDK against.
         * @note exposed publicly for use in util modules, etc...
         *
         */
        this.clientId = metadata.clientId || '';

        /**
         *
         * @access public
         * @type {String}
         * @since 1.1.2
         * @desc The environment that the SDK services should run in.
         * @note exposed publicly for use in util modules, etc...
         *
         */
        this.environment = metadata.environment || '';

        /**
         *
         * @access public
         * @type {String}
         * @since 2.0.0
         * @desc The SDK version. The format of the version is `SemVer`-`Built SHA`.
         * @example '3.9.0-23c141d'
         * @note using a shortened SHA String.
         *
         */
        this.version = metadata.version || '';

        /**
         *
         * @access public
         * @type {String}
         * @since 2.0.0
         * @desc The shortened version String, without the build SHA.
         * @example '3.9'
         *
         */
        this.versionShort = metadata.versionShort || '';

        /**
         *
         * @access public
         * @type {Boolean}
         * @desc Whether the service account was created with debug logging enabled.
         *
         */
        this.debugEnabled = metadata.debugEnabled || false;

        /**
         *
         * @access private
         * @type {SDK.Account.AccountManager}
         *
         */
        this.accountManager = this.managers.accountManager;

        /**
         *
         * @access private
         * @type {SDK.Media.MediaManager}
         *
         */
        this.mediaManager = this.managers.mediaManager;

        /**
         *
         * @access private
         * @type {SDK.Content.CommerceManager}
         *
         */
        this.commerceManager = this.managers.commerceManager;

        /**
         *
         * @access private
         * @type {SDK.Content.ContentManager}
         *
         */
        this.contentManager = this.managers.contentManager;

        /**
         *
         * @access private
         * @type {SDK.CustomerService.CustomerServiceManager}
         *
         */
        this.customerServiceManager = this.managers.customerServiceManager;

        /**
         *
         * @access private
         * @type {SDK.Token.TokenManager}
         *
         */
        this.tokenManager = this.managers.tokenManager;

        /**
         *
         * @access private
         * @type {SDK.Internal.Telemetry.TelemetryManager}
         *
         */
        this.telemetryManager = this.managers.telemetryManager;

        /**
         *
         * @access private
         * @type {SDK.Device.DeviceManager}
         *
         */
        this.deviceManager = this.managers.deviceManager;

        /**
         *
         * @access private
         * @type {SDK.Drm.DrmManager}
         * @since 3.2.0
         *
         */
        this.drmManager = this.managers.drmManager;

        /**
         *
         * @access private
         * @since 3.9.0
         * @type {SDK.Paywall.PaywallManager}
         *
         */
        this.paywallManager = this.managers.paywallManager;

        /**
         *
         * @access private
         * @type {SDK.Purchase.PurchaseManager}
         *
         */
        this.purchaseManager = this.managers.purchaseManager;

        /**
         *
         * @access private
         * @type {SDK.Subscription.SubscriptionManager}
         *
         */
        this.subscriptionManager = this.managers.subscriptionManager;

        /**
         *
         * @access private
         * @type {SDK.Identity.Bam.BamIdentityManager}
         *
         */
        this.bamIdentityManager = this.managers.bamIdentityManager;

        /**
         *
         * @access private
         * @type {SDK.Identity.Adobe.AdobeIdentityManager}
         *
         */
        this.adobeIdentityManager = this.managers.adobeIdentityManager;

        /**
         *
         * @access private
         * @type {SDK.Session.SessionManager}
         *
         */
        this.sessionManager = this.managers.sessionManager;

        /**
         *
         * @access public
         * @type {SDK.Logging.LoggingApi}
         * @desc Gets the Logging instance for access to logging related features.
         *
         */
        this.loggingApi = new LoggingApi(
            this.logger
        );

        /**
         *
         * @access public
         * @type {SDK.Media.MediaApi}
         * @desc Gets the media resource used to support media playback.
         *
         */
        this.mediaApi = new MediaApi(
            this.mediaManager,
            this.logger
        );

        /**
         *
         * @access public
         * @type {SDK.Content.ContentApi}
         * @desc Gets the content resource used to support content discovery.
         *
         */
        this.contentApi = new ContentApi(
            this.contentManager,
            this.logger
        );

        /**
         *
         * @access public
         * @type {SDK.Purchase.PurchaseApi}
         * @desc Gets the purchases resource used to support and retrieve in app purchases.
         *
         */
        this.purchaseApi = new PurchaseApi(
            this.purchaseManager,
            this.subscriptionManager,
            this.logger
        );

        /**
         *
         * @access public
         * @type {SDK.Subscription.SubscriptionApi}
         * @desc Gets the subscription resource used to support and retrieve subscriptions.
         *
         */
        this.subscriptionApi = new SubscriptionApi(
            this.subscriptionManager,
            this.logger
        );

        /**
         *
         * @access public
         * @type {SDK.Account.AccountApi}
         * @desc Gets the account resource to access account properties.
         *
         */
        this.accountApi = new AccountApi(
            this.accountManager,
            this.tokenManager,
            this.subscriptionManager,
            this.logger
        );

        /**
         *
         * @access public
         * @type {SDK.Identity.Bam.BamIdentityApi}
         * @desc Gets an object used to access BAM identity services.
         *
         */
        this.bamIdentityApi = new BamIdentityApi(
            this.bamIdentityManager,
            this.logger
        );

        /**
         *
         * @access public
         * @type {SDK.Identity.Adobe.AdobeIdentityApi}
         * @desc Gets an object used to access Adobe identity services.
         *
         */
        this.adobeIdentityApi = new AdobeIdentityApi(
            this.adobeIdentityManager,
            this.tokenManager,
            this.accountManager,
            this.logger
        );

        /**
         *
         * @access public
         * @type {SDK.UserActivity.UserActivityApi}
         * @desc Gets an object used to allow application developers to send custom events, related to
         * user activity while using an app.
         *
         */
        this.userActivityApi = new UserActivityApi(
            this.logger
        );

        /**
         *
         * @access public
         * @type {SDK.Commerce.CommerceApi}
         *
         */
        this.commerceApi = new CommerceApi(
            this.commerceManager,
            this.tokenManager,
            this.logger
        );

        /**
         *
         * @access public
         * @type {SDK.UserProfile.UserProfileApi}
         * @since 3.5.0
         * @desc Provides an object used to access and maintain user profiles.
         *
         */
        this.userProfileApi = new UserProfileApi(
            this.accountManager,
            this.tokenManager,
            this.logger
        );

        /**
         *
         * @access public
         * @type {SDK.Paywall.PaywallApi}
         * @since 3.9.0
         * @desc Provides an object used to access and maintain paywall.
         *
         */
        this.paywallApi = new PaywallApi({
            paywallManager: this.paywallManager,
            tokenManager: this.tokenManager,
            logger: this.logger
        });

        /**
         *
         * @access private
         * @type {Boolean}
         * @desc Determines if the current session instance is in a initialized state.
         *
         */
        this.isInitialized = false;

        /**
         *
         * @access private
         * @type {Boolean}
         * @desc flag used for enabling dust logging
         *
         */
        this.dustEnabled = true;

        this.logger.info(SdkSession.name, 'Created.');

    }

    /**
     *
     * @access public
     * @param {SDK.Services.Configuration.BootstrapConfiguration} bootstrapConfiguration - The bootstrap
     * information used to initialize the SDK.
     * @param {SDK.Token.GeoProvider} [geoProvider=null]
     * @param {SDK.Media.MediaCapabilitiesProvider} [mediaCapabilitiesProvider=null]
     * @desc Creates a new instance of `SdkSession` initialized with the supplied
     * bootstrapConfiguration information. This is the entry point into all further interactions with the SDK.
     * the provided BootstrapConfiguration information.
     * @throws {InvalidArgumentException}
     * @throws {ConfigurationNotFoundException} Unable to locate configuration data based on the supplied
     * bootstrap parameter.
     * @note The SDK config service could not find the requested config.
     * @throws {TemporarilyUnavailableException} Server is too busy and rejected request.
     * @note Any request returns HTTP 5xx.
     * @throws {NetworkException} Internet connection or timeout error.
     * @note Unable to contact service for any reason (no server response received).
     * @throws {ServiceException} An unexpected error has occurred.
     * @note Unexpected response from server. This could be an unexpected HTTP code or invalid or
     * missing response data. Most likely indicates a bug in the services or SDK.
     * @returns {Promise<SdkSession>}
     *
     * @example SDK.SdkSession.createSdkSession(bootstrapConfiguration).then((session) => {
     *     console.log(session.version); // '3.9.0-23c141d'
     * });
     *
     */
    static async createSdkSession(bootstrapConfiguration, geoProvider = null, mediaCapabilitiesProvider = null) {

        const errorMessage = {
            method: 'SdkSession.createSdkSession(bootstrapConfiguration, geoProvider, mediaCapabilitiesProvider)',
            processedDirective: 'The SDK should be loaded with a proper device runtime profile distribution.',
            get bootstrapConfiguration() {
                return `${this.method} needs SDK.Services.Configuration.BootstrapConfiguration instance`;
            }
        };

        instanceStrict(bootstrapConfiguration, BootstrapConfiguration, errorMessage.bootstrapConfiguration, InvalidArgumentException); // eslint-disable-line

        const SDK_VERSION = SdkSession.hasProcess() ? process.env.SDK_VERSION : '3.9.0';
        const SDK_VERSION_SHA = SdkSession.hasProcess() ? process.env.SDK_VERSION_SHA : '23c141d';
        const sdkVersion = `${SDK_VERSION}-${SDK_VERSION_SHA}`;
        const sdkVersionShort = SDK_VERSION && SDK_VERSION.substring(0, SDK_VERSION.lastIndexOf('.'));

        const { clientId, clientApiKey, environment, debugEnabled } = bootstrapConfiguration;
        const packageData = { sdkVersionShort };
        const logger = Logging;

        dustSink = new DustSink();

        logger.addSink(dustSink);
        logger.console(debugEnabled);
        logger.info(SdkSession.name, `Create SDK session instance, ${clientId}/${environment}.`);
        logger.info(SdkSession.name, `Version, ${sdkVersion}.`);
        logger.info(SdkSession.name, 'Debug mode enabled.');

        if (Check.equal(SdkSession.hasProcessedDirectives(), false)) {
            logger.error(SdkSession.name, errorMessage.processedDirectives);
        }

        // do not check for dustEnabled here because we need to log dust events until we get the config
        const dustLogUtility = new DustLogUtility({
            logger,
            source: SdkSession.name,
            urn: SdkSessionDustUrnReference.createSdkSession
        });

        const storage = new Storage(logger);
        const httpClient = new HttpClient(logger);
        const accessStorage = new AccessStorage(clientId, environment, logger, null, storage);
        const deviceGrantStorage = new DeviceGrantStorage(clientId, environment, logger, null, storage);
        const identityTokenStorage = new IdentityTokenStorage(clientId, environment, logger, storage);
        const configurationManager = new ConfigurationManager(bootstrapConfiguration, packageData, logger, storage);
        const accessTokenProvider = new AccessTokenProvider(accessStorage, logger);

        try {

            const sdkSessionConfiguration = await configurationManager.getConfiguration();
            const { services } = sdkSessionConfiguration;
            const managers = Object.create(null);

            managers.deviceManager = new DeviceManager(
                clientApiKey,
                services.device,
                new DeviceClient(services.device.client, logger, httpClient),
                logger,
                deviceGrantStorage,
                configurationManager.environmentConfiguration
            );

            managers.tokenManager = new TokenManager(
                clientApiKey,
                services.token,
                new TokenClient(services.token.client, logger, httpClient),
                geoProvider,
                logger,
                accessStorage,
                managers.deviceManager,
                sdkVersionShort
            );

            managers.telemetryManager = new TelemetryManager(
                services.telemetry,
                managers.tokenManager,
                new TelemetryClient(services.telemetry.client, logger, httpClient),
                logger
            );

            managers.accountManager = new AccountManager(
                services.account,
                new AccountClient(services.account.client, logger, httpClient),
                logger,
                managers.tokenManager
            );

            managers.purchaseManager = new PurchaseManager(
                services.purchase,
                new PurchaseClient(services.purchase.client, logger, httpClient),
                logger,
                managers.tokenManager
            );

            managers.drmManager = new DrmManager(
                services.drm,
                new DrmClient(services.drm.client, logger, httpClient),
                accessTokenProvider,
                clientId,
                environment,
                storage,
                logger
            );

            managers.adEngineManager = new AdEngineManager({
                config: services.adEngine,
                client: new AdEngineClient(services.adEngine.client, logger, httpClient),
                tokenManager: managers.tokenManager,
                sdkver: managers.deviceManager.environmentConfiguration.sdkVersion,
                logger,
                geoProvider
            });

            managers.mediaManager = new MediaManager({
                mediaManagerConfiguration: services.media,
                tokenManager: managers.tokenManager,
                drmManager: managers.drmManager,
                telemetryManager: managers.telemetryManager,
                mediaCapabilitiesProvider,
                mediaClient: new MediaClient(services.media.client, logger, httpClient),
                adEngineManager: managers.adEngineManager,
                logger
            });

            managers.contentManager = new ContentManager(
                services.content,
                new ContentClient(services.content.client, logger, httpClient),
                logger,
                accessTokenProvider
            );

            managers.subscriptionManager = new SubscriptionManager(
                services.subscription,
                new SubscriptionClient(services.subscription.client, logger, httpClient),
                logger,
                accessTokenProvider
            );

            managers.bamIdentityManager = new BamIdentityManager(
                services.bamIdentity,
                new BamIdentityClient(services.bamIdentity.client, logger, httpClient),
                logger,
                managers.tokenManager,
                identityTokenStorage,
            );

            managers.adobeIdentityManager = new AdobeIdentityManager(
                services.adobeIdentity,
                new AdobeIdentityClient(services.adobeIdentity.client, logger, httpClient),
                logger,
                accessTokenProvider
            );

            managers.sessionManager = new SessionManager(
                services.session,
                new SessionClient(services.session.client, logger, httpClient),
                logger,
                accessTokenProvider
            );

            managers.customerServiceManager = new CustomerServiceManager(
                services.customerService,
                new CustomerServiceClient(services.customerService.client, logger, httpClient),
                logger
            );

            managers.commerceManager = new CommerceManager(
                services.commerce,
                new CommerceClient(services.commerce.client, logger, httpClient),
                accessTokenProvider,
                logger
            );

            managers.paywallManager = new PaywallManager({
                config: services.paywall,
                client: new PaywallClient(services.paywall.client, logger, httpClient),
                tokenManager: managers.tokenManager,
                logger
            });

            const metadata = {
                clientId,
                clientApiKey,
                environment,
                debugEnabled,
                version: sdkVersion,
                versionShort: sdkVersionShort
            };

            const sdkSession = new SdkSession(
                sdkSessionConfiguration,
                accessStorage,
                logger,
                geoProvider,
                mediaCapabilitiesProvider,
                storage,
                httpClient,
                managers,
                metadata,
                accessTokenProvider
            );

            sdkSession.tokenManager.on('TokenRefreshFailed', (tokenRefreshFailure) => {
                logger.info(SdkSession.name, 'Dispatch ReauthorizationFailure event.');
                sdkSession.emit('ReauthorizationFailure', new ReauthorizationFailure(tokenRefreshFailure.error));
            });

            sdkSession.dustEnabled = !Util.getSafe(() => services.telemetry.disabled);

            Object.keys(managers).forEach((key) => {
                if (Util.getSafe(() => managers[key].client.dustEnabled)) {
                    managers[key].client.dustEnabled = sdkSession.dustEnabled;
                }
            });

            return Promise.resolve(sdkSession);

        } catch (ex) {

            return Promise.reject(dustLogUtility.captureErrors(ex));

        } finally {

            dustLogUtility.log();

        }

    }

    /**
     *
     * @access public
     * @desc Initializes the SDK and restores previous state if it exists.
     * @note If the operation fails, it can be retried or call reset to
     * reset the state and start in an anonymous state.
     * @note this.initialized MUST come before we try to initialize the DustSink
     * @throws {AuthenticationExpiredException} Could not reauthorize because authentication information
     * used to create authorization state has expired.
     * @note Request to exchange token returns HTTP 400 with error: invalid_grant and error_description: expired-token.
     * @throws {LocationNotAllowedException} User is not allowed to use the app in their current location.
     * Some products prohibit the use of the app in certain locations.
     * @note Request to exchange token returns HTTP 400 with error: unauthorized_client and
     * error_description: unknown-location, unreliable-location, or forbidden-location.
     * @throws {LocationAcquisitionException} Unable to get user location due to location service being disabled.
     * @note Only applicable on certain platforms.
     * @note GeoProvider throws exception while trying to acquire location.
     * @throws {TemporarilyUnavailableException} Server is too busy and rejected request.
     * @note Any request returns HTTP 5xx.
     * @throws {NetworkException} Internet connection or timeout error.
     * @note Unable to contact service for any reason (no server response received).
     * @throws {ServiceException} An unexpected error has occurred.
     * @note Unexpected response from server. This could be an unexpected HTTP code or invalid or
     * missing response data. Most likely indicates a bug in the services or SDK.
     * @returns {Promise<Void>} An awaitable action that returns when it succeeds.
     *
     * @example sdkSession.initialize().then() => {
     *     console.log(sdkSession.isInitialized); // true
     * });
     *
     */
    async initialize() {

        const { dustEnabled, logger } = this;

        logger.info(SdkSession.name, 'Initialize.');

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: SdkSession.name,
            urn: SdkSessionDustUrnReference.initialize
        });

        if (this.isInitialized) {

            logger.error(SdkSession.name, 'Already initialized.');

            const reasons = [new ErrorReason('', `${SdkSession.name} already initialized.`)];
            const exceptionData = ExceptionReference.common.invalidState;

            const exception = new ServiceException({ reasons, exceptionData });

            if (dustEnabled) {

                dustLogUtility.captureErrors(exception);

                try {

                    dustLogUtility.log();

                } catch (ex) {

                    return Promise.reject(exception);

                }

            }

            return Promise.reject(exception);

        }

        try {

            await this.reauthorize();

            logger.info(SdkSession.name, 'Successfully initialized.');

            this.isInitialized = true;

            if (dustEnabled) {

                try {
                    this.enableDustSink();
                } catch (ex) {
                    logger.error('SdkSession', 'DustSink not initialized', ex);
                }

            } else {

                dustSink.disableDustSink();

            }

            return Promise.resolve();

        } catch (ex) {

            logger.info(SdkSession.name, 'Dispatch ReauthorizationFailure event.');

            dustLogUtility.captureErrors(ex);

            this.emit('ReauthorizationFailure', new ReauthorizationFailure(ex));

            return Promise.reject(ex);

        } finally {

            if (dustEnabled) {
                dustLogUtility.log();
            }

        }

    }

    /**
     *
     * @access public
     * @desc Resets the SdkSession to an anonymous state.
     * @throws {LocationNotAllowedException} User is not allowed to use the app in their current location.
     * Some products prohibit the use of the app in certain locations.
     * @note Request to exchange token returns HTTP 400 with error: unauthorized_client and
     * error_description: unknown-location, unreliable-location, or forbidden-location.
     * @throws {LocationAcquisitionException} Unable to get user location due to location service being disabled.
     * @note Only applicable on certain platforms.
     * @note GeoProvider throws exception while trying to acquire location.
     * @throws {TemporarilyUnavailableException} Server is too busy and rejected request.
     * @note Any request returns HTTP 5xx.
     * @throws {NetworkException} Internet connection or timeout error.
     * @note Unable to contact service for any reason (no server response received).
     * @throws {ServiceException} An unexpected error has occurred.
     * @note Unexpected response from server. This could be an unexpected HTTP code or invalid or
     * missing response data. Most likely indicates a bug in the services or SDK.
     * @returns {Promise<Void>}
     *
     * @example sdkSession.reset().then() => {
     *     console.log('Reset SDK to original state');
     * });
     *
     */
    async reset() {

        const { dustEnabled, deviceManager, tokenManager, bamIdentityManager, logger } = this;

        logger.info(SdkSession.name, 'Reset SDK to an anonymous state.');

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: SdkSession.name,
            urn: SdkSessionDustUrnReference.reset
        });

        try {

            await bamIdentityManager.identityTokenStorage.clear();
            const deviceGrant = await deviceManager.getDeviceGrant();
            await tokenManager.reset(deviceGrant);

            this.isInitialized = true;

            return Promise.resolve();

        } catch (ex) {

            return dustEnabled ? Promise.reject(dustLogUtility.captureErrors(ex)) : Promise.reject(ex);

        } finally {

            if (dustEnabled) {
                dustLogUtility.log();
            }

        }

    }

    /**
     *
     * @access public
     * @desc Reauthorizes the current access of the SDK.
     * @throws {AuthenticationExpiredException} Could not reauthorize because authentication information
     * used to create authorization state has expired.
     * @note Request to exchange token returns HTTP 400 with error: invalid_grant and error_description: expired-token.
     * @throws {LocationNotAllowedException} User is not allowed to use the app in their current location.
     * Some products prohibit the use of the app in certain locations.
     * @note Request to exchange token returns HTTP 400 with error: unauthorized_client and
     * error_description: unknown-location, unreliable-location, or forbidden-location.
     * @throws {LocationAcquisitionException} Unable to get user location due to location service being disabled.
     * @note Only applicable on certain platforms.
     * @note GeoProvider throws exception while trying to acquire location.
     * @throws {TemporarilyUnavailableException} Server is too busy and rejected request.
     * @note Any request returns HTTP 5xx.
     * @throws {NetworkException} Internet connection or timeout error.
     * @note Unable to contact service for any reason (no server response received).
     * @throws {ServiceException} An unexpected error has occurred.
     * @note Unexpected response from server. This could be an unexpected HTTP code or invalid or
     * missing response data. Most likely indicates a bug in the services or SDK.
     * @returns {Promise<Void>}
     *
     * @example sdkSession.reauthorize().then() => {
     *     console.log('success');
     * });
     *
     */
    async reauthorize() {

        const { dustEnabled, deviceManager, tokenManager, logger, accountManager } = this;

        logger.info(SdkSession.name, 'Reauthorize the current access.');

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: SdkSession.name,
            urn: SdkSessionDustUrnReference.reauthorize
        });

        // check if an access context was stored in the previous session
        if (Check.assigned(tokenManager.getAccessToken())) {

            try {

                await tokenManager.refreshAccessToken(true);

                const storage = tokenManager.storage;
                const isLegacyToken = storage.isLegacyToken;
                const access = storage.access;

                if (isLegacyToken && Check.emptyArray(access.contextState.modes)) {

                    try {

                        // will fail if the token you are using is not associated with an account
                        await accountManager.getAccount();

                        // only give the user bamIdentity authorization if they make it here
                        access.contextState.modes.push('bamIdentity');

                        return await storage.saveAccess(access);

                    } catch (ex) {

                        if (dustEnabled) {
                            dustLogUtility.captureErrors(ex);
                        }

                        return Promise.resolve();

                    }

                }

                return Promise.resolve();

            } catch (ex) {

                return Promise.reject(ex);

            } finally {

                if (dustEnabled) {
                    dustLogUtility.log();
                }

            }

        }

        try {

            // no token, get or create device registration
            const deviceGrant = await deviceManager.getDeviceGrant();

            return await tokenManager.exchangeGrant(deviceGrant, new AccessContextState());

        } catch (ex) {

            return dustEnabled ? Promise.reject(dustLogUtility.captureErrors(ex)) : Promise.reject(ex);

        } finally {

            if (dustEnabled) {
                dustLogUtility.log();
            }

        }

    }

    /**
     *
     * @access public
     * @desc Returns a token appropriate for sharing with external services.
     * @note Returns the current access token. If the access token needs to be refreshed,
     * perform refresh before fulfilling the promise. Applications should not count on
     * this being a raw access token. The contract does not guarantee an access token.
     * Warning: Applications should not store this token, since the token
     * lifecycle is managed by the SDK. Call this method immediately before use.
     * Applications should not assume any use cases with this token,
     * or try to use it to hit BAM services directly.
     * @throws {AuthenticationExpiredException} Could not reauthorize because authentication information
     * used to create authorization state has expired.
     * @note Request to exchange token returns HTTP 400 with error: invalid_grant and error_description: expired-token.
     * @throws {LocationNotAllowedException} User is not allowed to use the app in their current location.
     * Some products prohibit the use of the app in certain locations.
     * @note Request to exchange token returns HTTP 400 with error: unauthorized_client and
     * error_description: unknown-location, unreliable-location, or forbidden-location.
     * @throws {LocationAcquisitionException} Unable to get user location due to location service being disabled.
     * @note Only applicable on certain platforms.
     * @note GeoProvider throws exception while trying to acquire location.
     * @throws {TemporarilyUnavailableException} Server is too busy and rejected request.
     * @note Any request returns HTTP 5xx.
     * @throws {NetworkException} Internet connection or timeout error.
     * @note Unable to contact service for any reason (no server response received).
     * @throws {ServiceException} An unexpected error has occurred.
     * @note Unexpected response from server. This could be an unexpected HTTP code or invalid or
     * missing response data. Most likely indicates a bug in the services or SDK.
     * @returns {Promise<String>}
     *
     * @example sdkSession.getSessionToken().then((sessionToken) => {
     *     console.log(sessionToken);
     * });
     *
     */
    async getSessionToken() {

        const { dustEnabled, accessTokenProvider, logger } = this;

        logger.info(SdkSession.name, 'Get current access token.');

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: SdkSession.name,
            urn: SdkSessionDustUrnReference.reauthorize
        });

        const accessToken = accessTokenProvider.getAccessToken();
        const token = Util.getSafe(() => accessToken.token);

        if (dustEnabled) {
            dustLogUtility.log();
        }

        return Promise.resolve(token);

    }

    /**
     *
     * @access public
     * @desc Gets the access state of the user in a format that
     * can be used to rebuild the state in an external system.
     * @note Contains version information as well as access state data;
     * including the token, refreshToken, and access context state.
     * @returns {Promise<String>}
     *
     * @example sdkSession.getAccessState().then((accessState) => {
     *     console.log(accessState); // serialized AccessState
     * });
     *
     */
    async getAccessState() {

        const { dustEnabled, tokenManager, logger } = this;

        logger.info(SdkSession.name, 'Get access state.');

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: SdkSession.name,
            urn: SdkSessionDustUrnReference.reauthorize
        });

        const accessState = JSON.stringify(tokenManager.getAccessState());

        if (dustEnabled) {
            dustLogUtility.log();
        }

        return Promise.resolve(accessState);

    }

    /**
     *
     * @access public
     * @param {String} accessState - The serialized state retrieved from another
     * device via SdkSession#getAccessState
     * @desc Initializes the SDK and restores the state retrieved from another device.
     * @note If the operation fails, it can be retried or call SdkSession#reset to
     * reset the state and start in an anonymous state.
     * @throws {AuthenticationExpiredException} Could not reauthorize because authentication information
     * used to create authorization state has expired.
     * @note Request to exchange token returns HTTP 400 with error: invalid_grant and error_description: expired-token.
     * @throws {LocationNotAllowedException} User is not allowed to use the app in their current location.
     * Some products prohibit the use of the app in certain locations.
     * @note Request to exchange token returns HTTP 400 with error: unauthorized_client and
     * error_description: unknown-location, unreliable-location, or forbidden-location.
     * @throws {LocationAcquisitionException} Unable to get user location due to location service being disabled.
     * @note Only applicable on certain platforms.
     * @note GeoProvider throws exception while trying to acquire location.
     * @throws {TemporarilyUnavailableException} Server is too busy and rejected request.
     * @note Any request returns HTTP 5xx.
     * @throws {NetworkException} Internet connection or timeout error.
     * @note Unable to contact service for any reason (no server response received).
     * @throws {ServiceException} An unexpected error has occurred.
     * @note Unexpected response from server. This could be an unexpected HTTP code or invalid or
     * missing response data. Most likely indicates a bug in the services or SDK.
     * @returns {Promise<Void>}
     *
     * @example sdkSession.restoreAccessState(accessState).then() => {
     *     console.log(sdkSession.isInitialized); // true
     * });
     *
     */
    async restoreAccessState(accessState) {

        const { dustEnabled, logger } = this;

        const errorMessage = {
            method: 'SdkSession.restoreAccessState(accessState)',
            get accessState() {
                return `${this.method} accessState should be a String with length`;
            }
        };

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: SdkSession.name,
            urn: SdkSessionDustUrnReference.restoreAccessState
        });

        nonEmptyString(accessState, errorMessage.accessState, InvalidArgumentException);

        const { tokenManager } = this;

        logger.info(SdkSession.name, 'Restore access state from another device or SDK instance.');

        if (this.isInitialized) {

            logger.warn(SdkSession.name, 'Already initialized.');

            const reasons = [new ErrorReason('', `${SdkSession.name} already initialized.`)];
            const exceptionData = ExceptionReference.common.invalidState;

            const exception = new ServiceException({ reasons, exceptionData });

            if (dustEnabled) {
                dustLogUtility.captureErrors(exception);
                dustLogUtility.log();
            }

            return Promise.reject(exception);

        }

        try {

            await tokenManager.restoreAccessState(accessState);

            this.isInitialized = true;

            return Promise.resolve();

        } catch (ex) {

            return dustEnabled ? Promise.reject(dustLogUtility.captureErrors(ex)) : Promise.reject(ex);

        } finally {

            if (dustEnabled) {
                dustLogUtility.log();
            }

        }

    }

    /**
     *
     * @access public
     * @since 3.5.0
     * @param {String} grant
     * @param {String} provider - An unique key representing the authorization type
     * @desc Authorizes the session with an external account that was authenticated and created by an external provider.
     * @throws {LocationNotAllowedException} User is not allowed to use the app in their current location.
     * Some products prohibit the use of the app in certain locations.
     * @note Request to exchange token returns HTTP 400 with error: unauthorized_client and
     * error_description: unknown-location, unreliable-location, or forbidden-location.
     * @throws {LocationAcquisitionException} Unable to get user location due to location service being disabled.
     * @note Only applicable on certain platforms.
     * @note GeoProvider throws exception while trying to acquire location.
     * @throws {TemporarilyUnavailableException} Server is too busy and rejected request.
     * @note Any request returns HTTP 5xx.
     * @throws {NetworkException} Internet connection or timeout error.
     * @note Unable to contact service for any reason (no server response received).
     * @throws {ServiceException} An unexpected error has occurred.
     * @note Unexpected response from server. This could be an unexpected HTTP code or invalid or
     * missing response data. Most likely indicates a bug in the services or SDK.
     * @returns {Promise<Void>}
     *
     */
    authorize(grant, provider) {

        const errorMessage = {
            method: 'SdkSession.authorize(grant, provider)',
            get grant() {
                return `${this.method} grant should be a String with length`;
            },
            get provider() {
                return `${this.method} provider should be a String with length`;
            }
        };

        nonEmptyString(grant, errorMessage.grant, InvalidArgumentException);
        nonEmptyString(provider, errorMessage.providerError, InvalidArgumentException);

        const { tokenManager } = this;

        const accessContextState = new AccessContextState([provider]);
        const accountGrant = new AccountGrant({ assertion: grant });

        return tokenManager.exchangeGrant(accountGrant, accessContextState);

    }

    /**
     *
     * @access public
     * @param {String} provider - An unique key representing the authorization type (i.e. 'espn').
     * @desc Tests whether the SDK is currently in an "authorized" state for the given authorization type.
     * @returns {Promise<Boolean>} A boolean indicating whether or not the SDK is
     * currently authorized for the given type of authorization.
     *
     * @example sdkSession.isAuthorized('espn').then((isAuthorized) => {
     *     console.log(isAuthorized); // true or false
     * });
     *
     */
    async isAuthorized(provider) {

        const errorMessage = {
            method: 'SdkSession.isAuthorized(provider)',
            get provider() {
                return `${this.method} provider should be a String with length`;
            }
        };

        nonEmptyString(provider, errorMessage.provider, InvalidArgumentException);

        const { tokenManager, logger } = this;

        logger.info(SdkSession.name, `Check authorization state for "${provider}"`);

        return await tokenManager.hasAccessMode(provider);

    }

    /**
     *
     * @access public
     * @since 3.1.0
     * @desc Gets information about the current session.
     * @throws {AuthenticationExpiredException} Could not reauthorize because authentication information
     * used to create authorization state has expired.
     * @note Request to exchange token returns HTTP 400 with error: invalid_grant and error_description: expired-token.
     * @throws {LocationNotAllowedException} User is not allowed to use the app in their current location.
     * Some products prohibit the use of the app in certain locations.
     * @note Request to exchange token returns HTTP 400 with error: unauthorized_client and
     * error_description: unknown-location, unreliable-location, or forbidden-location.
     * @throws {LocationAcquisitionException} Unable to get user location due to location service being disabled.
     * @note Only applicable on certain platforms.
     * @note GeoProvider throws exception while trying to acquire location.
     * @throws {TemporarilyUnavailableException} Server is too busy and rejected request.
     * @note Any request returns HTTP 5xx.
     * @throws {NetworkException} Internet connection or timeout error.
     * @note Unable to contact service for any reason (no server response received).
     * @throws {ServiceException} An unexpected error has occurred.
     * @note Unexpected response from server. This could be an unexpected HTTP code or invalid or
     * missing response data. Most likely indicates a bug in the services or SDK.
     * @returns {Promise<SDK.Services.Session.SessionInfo>}
     *
     * @example sdkSession.getSessionInfo().then((sessionInfo) => {
     *     console.log(sessionInfo.device);
     *     console.log(sessionInfo.location);
     *     console.log(sessionInfo.entitlements);
     * });
     *
     */
    async getSessionInfo() {

        const { dustEnabled, sessionManager, logger } = this;

        logger.info(SdkSession.name, 'Get session information.');

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: AccountApi.name,
            urn: SdkSessionDustUrnReference.getSessionInfo
        });

        try {

            return await sessionManager.getInfo();

        } catch (ex) {

            return dustEnabled ? Promise.reject(dustLogUtility.captureErrors(ex)) : Promise.reject(ex);

        } finally {

            if (dustEnabled) {
                dustLogUtility.log();
            }

        }

    }

    /**
     *
     * @access public
     * @desc Creates a code to show the end user when an error is displayed.
     * @note This code can be used to find a customer's current context (such as
     * device ID).
     * @throws {TemporarilyUnavailableException} Server is too busy and rejected request.
     * @note Any request returns HTTP 5xx.
     * @throws {NetworkException} Internet connection or timeout error.
     * @note Unable to contact service for any reason (no server response received).
     * @throws {ServiceException} An unexpected error has occurred.
     * @note Unexpected response from server. This could be an unexpected HTTP code or invalid or
     * missing response data. Most likely indicates a bug in the services or SDK.
     * @returns {Promise<String>} A Customer Service Support Code.
     *
     */
    async createCustomerSupportCode() {

        const { dustEnabled, customerServiceManager, accessTokenProvider, logger } = this;

        logger.info(SdkSession.name, 'Get customer service support code.');

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: SdkSession.name,
            urn: SdkSessionDustUrnReference.createCustomerSupportCode
        });

        const accessToken = accessTokenProvider.getAccessToken();

        try {

            return await customerServiceManager.createSupportCode(accessToken);

        } catch (ex) {

            return dustEnabled ? Promise.reject(dustLogUtility.captureErrors(ex)) : Promise.reject(ex);

        } finally {

            if (dustEnabled) {
                dustLogUtility.log();
            }

        }

    }

    /* #region private: */

    /**
     *
     * @access private
     * @desc sets up dust logging if it is enabled
     *
     */
    enableDustSink() {

        const { managers } = this;
        const { deviceManager, telemetryManager } = managers;

        const environmentConfiguration = deviceManager.environmentConfiguration;

        dustSink.enableDustSink(telemetryManager, {
            device: {
                os: environmentConfiguration.deviceRuntimeProfile,
                model: environmentConfiguration.deviceProfile
            },
            sdk: {
                version: environmentConfiguration.sdkVersion,
                platform: environmentConfiguration.platform
            }
        });

    }

    /**
     *
     * @access private
     * @since 2.1.0
     * @desc determines if running in a Node process
     * @returns {Boolean}
     *
     */
    static hasProcess() {

        if ((typeof process !== 'undefined') && (process.env !== 'undefined')) {
            return true;
        }

        return false;

    }

    /**
     *
     * @access private
     * @since 2.0.1
     * @see https://webpack.js.org/plugins/define-plugin/#usage
     * @see https://nodejs.org/api/process.html#process_process_env
     * @desc determines if the SDK was loaded with a proper device runtime profile
     * distribution by checking if the expected global directives are populated
     * @returns {Boolean}
     *
     */
    static hasProcessedDirectives() {

        if (Check.equal(SdkSession.hasProcess(), false)) {
            return false;
        }

        const SDK_VERSION = process.env.SDK_VERSION;
        const SDK_VERSION_SHA = process.env.SDK_VERSION_SHA;
        const device_family = process.env.device_family;
        const device_profile = process.env.device_profile;
        const application_runtime = process.env.application_runtime;

        const directives = [
            SDK_VERSION,
            SDK_VERSION_SHA,
            device_family,
            device_profile,
            application_runtime
        ];

        return Check.all(Check.apply(directives, Check.nonEmptyString));

    }

}



// WEBPACK FOOTER //
// ./src/sdkSession.js